import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
} from '@mui/material';
import { Lock as LockIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { validatePin } from '../utils/validation';

const VerifyPin: React.FC = () => {
  const [pin, setPin] = useState<string>('');
  const [pinError, setPinError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      // Redirect to login if no email is provided
      navigate('/login');
    }
  }, [location.state, navigate]);

  const validatePinField = (pin: string): boolean => {
    const error = validatePin(pin);
    setPinError(error);
    return error === '';
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers and limit to 6 digits
    if (/^\d{0,6}$/.test(value)) {
      setPin(value);
      if (pinError) {
        validatePinField(value);
      }
    }
  };

  const handleVerifyPin = async () => {
    if (!validatePinField(pin)) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await authService.validatePin({
        source: 'login',
        email,
        pin: parseInt(pin, 10),
      });

      if (response.succeeded) {
        // Store tokens and navigate to dashboard
        await login({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        });
        navigate('/');
      } else {
        setError(response.errorDescription || 'Invalid PIN. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('PIN validation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (!email) {
    return null; // Don't render until email is loaded
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Verify Your PIN
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Enter the 6-digit code sent to
            </Typography>
            <Typography variant="body1" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {email}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="6-Digit PIN"
            value={pin}
            onChange={handlePinChange}
            onBlur={() => validatePinField(pin)}
            error={!!pinError}
            helperText={pinError}
            placeholder="Enter 6-digit code"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
            }}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleVerifyPin}
            disabled={isLoading || !pin || !!pinError}
            startIcon={isLoading ? <CircularProgress size={20} /> : <LockIcon />}
            sx={{ mb: 2, py: 1.5 }}
          >
            {isLoading ? 'Verifying...' : 'Verify PIN'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToLogin}
            startIcon={<ArrowBackIcon />}
            sx={{ py: 1.5 }}
          >
            Back to Login
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Didn't receive the code? Check your email or try again
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyPin; 